import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';

const DEDUCTION_TYPES = [
  { value: 'cycle_to_work', label: 'Cycle to Work Scheme' },
  { value: 'season_ticket_loan', label: 'Season Ticket Loan' },
  { value: 'other', label: 'Other' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Generate rolling 24 months from current month
function generateFutureMonths() {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      shortLabel: `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${String(d.getFullYear()).slice(2)}`,
    });
  }
  return months;
}

export default function DeductionModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const futureMonths = generateFutureMonths();

  const [form, setForm] = useState({
    deduction_type: 'cycle_to_work',
    amount: '',
    deduct_from: 'net',
    apply_month: futureMonths[0]?.key || '',
    recurring: false,
    recurring_end_date: '',
    notes: '',
    // Bespoke fields for cycle_to_work and season_ticket_loan
    total_amount: '',
    monthly_deduction: '',
    deduction_months: [], // now stores keys like "2026-11"
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isBespoke = form.deduction_type === 'cycle_to_work' || form.deduction_type === 'season_ticket_loan';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError('');
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleMonth = (monthKey) => {
    setForm(prev => {
      const months = prev.deduction_months.includes(monthKey)
        ? prev.deduction_months.filter(m => m !== monthKey)
        : [...prev.deduction_months, monthKey].sort();
      // Auto-calculate monthly deduction
      const total = parseFloat(prev.total_amount) || 0;
      const monthlyDeduction = months.length > 0 && total > 0
        ? (total / months.length).toFixed(2)
        : prev.monthly_deduction;
      return { ...prev, deduction_months: months, monthly_deduction: monthlyDeduction };
    });
  };

  const handleTotalAmountChange = (e) => {
    const total = parseFloat(e.target.value) || 0;
    setForm(prev => {
      const monthlyDeduction = prev.deduction_months.length > 0 && total > 0
        ? (total / prev.deduction_months.length).toFixed(2)
        : '';
      return { ...prev, total_amount: e.target.value, monthly_deduction: monthlyDeduction };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isBespoke) {
      if (!form.total_amount || parseFloat(form.total_amount) <= 0) {
        setError('Please enter the total amount to be deducted');
        return;
      }
      if (form.deduction_months.length === 0) {
        setError('Please select at least one month for deductions');
        return;
      }
    } else {
      if (!form.amount || parseFloat(form.amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }
    }
    if (!form.deduction_type) {
      setError('Please select a deduction type');
      return;
    }
    setSaving(true);
    try {
      const selectedMonthLabels = form.deduction_months.map(key => {
        const fm = futureMonths.find(m => m.key === key);
        return fm ? fm.label : key;
      });

      const saveData = isBespoke
        ? {
            deduction_type: form.deduction_type,
            total_amount: parseFloat(form.total_amount).toFixed(2),
            monthly_deduction: form.monthly_deduction,
            amount: form.monthly_deduction, // for display compatibility
            deduction_months: form.deduction_months,
            deduction_months_labels: selectedMonthLabels,
            notes: form.notes,
            deduction_type_label: DEDUCTION_TYPES.find(t => t.value === form.deduction_type)?.label || form.deduction_type,
          }
        : {
            ...form,
            amount: parseFloat(form.amount).toFixed(2),
            deduction_type_label: DEDUCTION_TYPES.find(t => t.value === form.deduction_type)?.label || form.deduction_type,
            recurring_end_date: form.recurring && form.recurring_end_date ? form.recurring_end_date : null,
            apply_month_label: futureMonths.find(m => m.key === form.apply_month)?.label || null,
          };
      // Remove internal bespoke fields from non-bespoke saves
      if (!isBespoke) {
        delete saveData.total_amount;
        delete saveData.monthly_deduction;
        delete saveData.deduction_months;
        delete saveData.deduction_months_labels;
      }

      await onSave(employee.id, saveData);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Selected month labels for summary
  const selectedLabels = form.deduction_months.map(key => {
    const fm = futureMonths.find(m => m.key === key);
    return fm ? fm.shortLabel : key;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Deduction - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Deduction Type *</label>
              <select name="deduction_type" value={form.deduction_type} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                {DEDUCTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {isBespoke ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Amount to Deduct *</label>
                  <div className="mt-1 relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                    <input type="number" name="total_amount" step="0.01" min="0" inputMode="decimal" value={form.total_amount} onChange={handleTotalAmountChange}
                      className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="0.00" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {form.deduction_type === 'cycle_to_work'
                      ? 'Total value of the cycle to work scheme to be deducted from salary'
                      : 'Total season ticket loan amount to be repaid'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Deduct Over Which Months *</label>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                    {futureMonths.map(month => {
                      const isSelected = form.deduction_months.includes(month.key);
                      return (
                        <button
                          key={month.key}
                          type="button"
                          onClick={() => toggleMonth(month.key)}
                          className={`text-xs py-2 px-1 rounded-md border transition-colors ${
                            isSelected
                              ? 'bg-brand-600 text-white border-brand-600 font-semibold'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-brand-300'
                          }`}
                        >
                          {month.shortLabel}
                        </button>
                      );
                    })}
                  </div>
                  {form.deduction_months.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      {form.deduction_months.length} month{form.deduction_months.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {form.total_amount && form.deduction_months.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
                    <span className="font-medium">&pound;{form.monthly_deduction}</span> will be deducted each month over{' '}
                    <span className="font-medium">{form.deduction_months.length} month{form.deduction_months.length !== 1 ? 's' : ''}</span>
                    <span className="block text-xs mt-1">{selectedLabels.join(', ')}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Deduct from *</label>
                  <select name="deduct_from" value={form.deduct_from} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <option value="net">Net pay (after tax &amp; NI)</option>
                    <option value="pre_tax">Pre-tax (before tax &amp; NI)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Most miscellaneous deductions (overpayments, staff purchases) come off net take-home pay. Use pre-tax only for salary-sacrifice arrangements.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount per Pay Period *</label>
                  <div className="mt-1 relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                    <input type="number" name="amount" step="0.01" min="0" inputMode="decimal" value={form.amount} onChange={handleChange}
                      className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="0.00" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Apply in</label>
                  <select name="apply_month" value={form.apply_month} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    {futureMonths.map(m => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {form.recurring ? 'The pay run this deduction starts from.' : 'The pay run this deduction applies to.'}
                  </p>
                </div>

                <label className="flex items-center space-x-2">
                  <input type="checkbox" name="recurring" checked={form.recurring} onChange={handleChange}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Recurring (applies each pay period until end date or cancelled)</span>
                </label>

                {form.recurring && (
                  <div className="ml-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End date (optional)</label>
                    <DateInput name="recurring_end_date" value={form.recurring_end_date} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave blank if open-ended — you can cancel it later from the dashboard.
                    </p>
                  </div>
                )}
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="3" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Any additional details about this deduction..." />
            </div>

            <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded text-sm text-red-700 dark:text-red-300">
              {isBespoke
                ? `A total of \u00A3${form.total_amount || '0.00'} will be deducted from ${employee.first_name} ${employee.last_name}'s payroll${form.deduction_months.length > 0 ? ` over ${form.deduction_months.length} months` : ''}.`
                : `A deduction of \u00A3${form.amount || '0.00'} will be applied to ${employee.first_name} ${employee.last_name}'s next payroll.`
              }
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Deduction'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
